"use client";

import { Button } from "@/components/ui/button";
import { safeJsonParse } from "@/lib/safeJsonParse";
import { cn } from "@/lib/utils";
import { Cross2Icon } from "@radix-ui/react-icons";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
import "filepond/dist/filepond.min.css";
import { memo, useCallback } from "react";
import { FilePond, registerPlugin } from "react-filepond";

registerPlugin(FilePondPluginImagePreview);

interface ImageUploaderProps {
  image?: any;
  onImageChange: (image: any) => void;
  labelIdle?: string;
  className?: string;
  showRemoveButton?: boolean;
  removeButtonClassName?: string;
  serverUrl?: string;
}

const ImageUploader = memo(function ImageUploader({
  image,
  onImageChange,
  labelIdle = '<span class="filepond--label-action">Click here or drag file in to add your image</span>',
  className = "rounded-xl overflow-hidden cursor-pointer h-full min-h-64 bg-background/30",
  showRemoveButton = true,
  removeButtonClassName = "text-red-50 absolute top-3.5 right-7 bg-red-500/20 border border-red-500/30 rounded-full h-8 w-8 hover:bg-red-500 p-1",
  serverUrl,
}: ImageUploaderProps) {
  const handleProcessFile = useCallback(
    (error: any, file: any) => {
      if (!error) {
        const parsedResponse = safeJsonParse(file.serverId);
        onImageChange(parsedResponse);
      } else {
        console.error(error);
      }
    },
    [onImageChange],
  );

  const handleRemoveImage = useCallback(() => {
    onImageChange(null);
  }, [onImageChange]);

  const handleFileProcessingError = useCallback((error: any) => {
    console.log("error", error);
    if (error.code === 413)
      return "The file is too large (>10MB). Please upload a smaller file.";
    return "There was an error processing your file.";
  }, []);

  const files = image?.filename
    ? [
        {
          source: image.filename,
          options: {
            type: "local" as const,
          },
        },
      ]
    : [];

  return (
    <div className="relative">
      <FilePond
        files={files}
        onprocessfile={handleProcessFile}
        allowMultiple={false}
        maxFiles={1}
        server={{
          url: serverUrl || process.env.BASE_FILE_BUCKET_URL + "/storage",
          process: "/upload",
          load: "/",
          fetch: null,
        }}
        name="file"
        labelIdle={labelIdle}
        className={className}
        labelFileProcessingError={handleFileProcessingError}
      />
      {showRemoveButton && image?.filename && (
        <Button
          onClick={handleRemoveImage}
          variant="ghost"
          size="icon"
          className={cn("absolute", removeButtonClassName)}
        >
          <Cross2Icon />
        </Button>
      )}
    </div>
  );
});

export default ImageUploader;
